import { useCallback, useEffect, useRef, useState } from "react";
import {
	type NativeScrollEvent,
	type NativeSyntheticEvent,
	ScrollView,
	View,
} from "react-native";

import { useAppTheme } from "@hooks/useAppTheme";
import { Button, Icon, IconButton, Text } from "react-native-paper";

import { useActiveScenario } from "../hooks/useActiveScenario";
import type { TranscriptMessage } from "../types";
import { MessageBubble } from "./MessageBubble";

// How close (px) to the bottom still counts as "pinned" — scroll anchoring
// tolerance so sub-pixel rounding and momentum don't unpin the thread.
const PINNED_THRESHOLD = 48;

export const ConversationThread = ({
	messages,
	isStreaming,
	onStartConversation,
}: {
	messages: TranscriptMessage[];
	isStreaming: boolean;
	onStartConversation?: () => void;
}) => {
	const ref = useRef<ScrollView>(null);
	// Pinned = user is at (or near) the bottom, so new content may auto-scroll.
	// A ref, not state: it changes on every scroll frame and must never re-render.
	const pinnedRef = useRef(true);
	const lastOffsetYRef = useRef(0);
	const lastMessageIdRef = useRef<string | undefined>(undefined);
	const [showJump, setShowJump] = useState(false);
	const theme = useAppTheme();
	const { scenario, registry } = useActiveScenario();
	const personaName = scenario.persona.name;
	const avatarRole = registry.avatarRole;

	// The rAF defers the scroll until after the new content/layout has painted,
	// which makes scrollToEnd reliable on web (calling it synchronously from the
	// event often no-ops).
	const scrollToBottom = useCallback(
		(animated: boolean) =>
			requestAnimationFrame(() => ref.current?.scrollToEnd({ animated })),
		[],
	);

	const handleScroll = (e: NativeSyntheticEvent<NativeScrollEvent>) => {
		const { contentOffset, contentSize, layoutMeasurement } = e.nativeEvent;
		const distanceFromBottom =
			contentSize.height - layoutMeasurement.height - contentOffset.y;
		// Unpin only on an upward scroll (user intent). Streamed content growth
		// raises distanceFromBottom while the offset stays put, and the corrective
		// scrollToEnd lands one rAF later — a distance-only check unpins in that
		// gap and kills auto-follow mid-stream.
		const scrolledUp = contentOffset.y < lastOffsetYRef.current;
		lastOffsetYRef.current = contentOffset.y;
		if (distanceFromBottom <= PINNED_THRESHOLD) {
			pinnedRef.current = true;
			setShowJump(false);
		} else if (scrolledUp) {
			pinnedRef.current = false;
		}
	};

	// Instant scroll while content streams in — repeated animated scrollToEnd
	// calls cancel each other on web and the thread never reaches the bottom.
	const handleContentGrowth = () => {
		if (pinnedRef.current) {
			scrollToBottom(false);
		} else {
			setShowJump(true);
		}
	};

	// One smooth settle once the stream finishes, only if still pinned.
	useEffect(() => {
		if (!isStreaming && pinnedRef.current) {
			requestAnimationFrame(() => ref.current?.scrollToEnd({ animated: true }));
		}
	}, [isStreaming]);

	// Sending your own message always snaps to the bottom and re-pins, even if
	// scrolled up — so the reply that follows streams while pinned.
	const lastMessage = messages[messages.length - 1];
	useEffect(() => {
		if (lastMessage?.id === lastMessageIdRef.current) {
			return;
		}
		lastMessageIdRef.current = lastMessage?.id;
		if (lastMessage?.role === "mentor") {
			pinnedRef.current = true;
			setShowJump(false);
			scrollToBottom(false);
		}
	}, [lastMessage, scrollToBottom]);

	const jumpToLatest = () => {
		pinnedRef.current = true;
		setShowJump(false);
		scrollToBottom(true);
	};

	if (messages.length === 0) {
		return (
			<View
				style={{
					flex: 1,
					backgroundColor: theme.colors.surface,
					alignItems: "center",
					justifyContent: "center",
					paddingHorizontal: 24,
				}}
			>
				<Icon
					source="comment-multiple-outline"
					size={48}
					color={theme.colors.onSurfaceVariant}
				/>
				<Text
					variant="titleMedium"
					style={{
						textAlign: "center",
						color: theme.colors.onSurface,
						marginTop: 12,
					}}
				>
					Start Conversation
				</Text>
				<Text
					variant="bodyMedium"
					style={{
						textAlign: "center",
						color: theme.colors.onSurfaceVariant,
						maxWidth: 360,
						marginTop: 4,
					}}
				>
					Type or speak a message below to start the conversation with{" "}
					{personaName}
				</Text>
				<Button
					mode="contained-tonal"
					icon="plus"
					style={{ marginTop: 16 }}
					accessibilityLabel="New message"
					onPress={onStartConversation}
				>
					New Message
				</Button>
			</View>
		);
	}

	const lastIndex = messages.length - 1;
	// The avatar bubble is created lazily on the first streamed token, so show a
	// standalone typing bubble while waiting for the reply to begin.
	const awaitingReply = isStreaming && messages[lastIndex]?.role === "mentor";

	return (
		<View style={{ flex: 1 }}>
			<ScrollView
				ref={ref}
				style={{ flex: 1, backgroundColor: theme.colors.surface }}
				contentContainerStyle={{ paddingVertical: 16 }}
				scrollEventThrottle={16}
				onScroll={handleScroll}
				onContentSizeChange={handleContentGrowth}
				// Viewport resize — e.g. the feedback strip below the thread
				// appearing — must also keep the latest message visible.
				onLayout={() => pinnedRef.current && scrollToBottom(false)}
			>
				<View
					style={{
						width: "100%",
						paddingHorizontal: 12,
					}}
				>
					{messages.map((m, i) => (
						<MessageBubble
							key={m.id}
							message={m}
							isStreaming={
								isStreaming && i === lastIndex && m.role !== "mentor"
							}
						/>
					))}
					{awaitingReply ? (
						<MessageBubble
							message={{ id: "avatar-typing", role: avatarRole, text: "" }}
							isStreaming
						/>
					) : null}
				</View>
			</ScrollView>
			{showJump ? (
				<IconButton
					icon="arrow-down"
					mode="contained"
					size={20}
					containerColor={theme.colors.secondaryContainer}
					iconColor={theme.colors.onSecondaryContainer}
					style={{ position: "absolute", bottom: 12, right: 16, margin: 0 }}
					accessibilityLabel="Scroll to latest message"
					onPress={jumpToLatest}
				/>
			) : null}
		</View>
	);
};
